<template>
    <div :class="classList">
        <div class="show-picker-cta-container">
            <ButtonCta :buttonConfig="btnNextConfig"></ButtonCta>
        </div>

        <div class="showpicker-scroll-container">
            <div class="show-picker-header">
                <div class="avimg-show-picker">
                    <div class="avatar-show-picker" v-if="avatarUrl">
                        <div class="avimg-show-picker">
                            <AvatarImage :src="avatarUrl" loading="lazy" />
                        </div>
                    </div>
                </div>
                <div class="show-picker-header-left">
                    <div class="show-picker--title">{{ title }}</div>
                    <div class="show-picker--description">{{ description }}</div>
                </div>
            </div>

            <div class="show-picker-grid grid portrait">
                <ShowSelectItem
                    v-for="show in recommendedShows"
                    :key="show.showId"
                    :show="show"
                    :isSelected="selectedShowIds.indexOf(show.showId) >= 0"
                    v-on:showItemSelected="onShowItemSelected"
                    :isColdStartPremiumShowPickerEnabled="
                        isColdStartPremiumShowPickerEnabled
                    "></ShowSelectItem>
            </div>
        </div>

        <video
            v-if="loadLandscape"
            class="hide-for-pre-cache"
            id="landscape-video-Background"
            loop
            muted
            autoplay
            playsinline>
            <source :src="backgroundVideoLandscape" type="video/mp4" />
        </video>

        <video
            v-if="loadPortrait"
            class="hide-for-pre-cache"
            id="portrait-video-Background"
            loop
            muted
            autoplay
            playsinline>
            <source :src="backgroundVideoPortrait" type="video/mp4" />
        </video>

        <img v-if="loadLandscape" :src="landscapePoster" class="hide-for-pre-cache" />

        <img v-if="loadPortrait" :src="portraitPoster" class="hide-for-pre-cache" />

        <div v-if="notSafari" class="show-picker-bottom-fade"></div>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import HttpService from 'services/HttpServiceBasic';
    import AvatarImage from 'components/userProfile/res/AvatarImage';
    import ButtonCta from 'atoms/ButtonCta';
    import ShowSelectItem from 'components/ShowsPicker/ShowSelectItem';
    import { COLD_START_PREMIUM_LOCKS_ENABLED } from '../../helpers/featureConstants';
    import { FLOW_TYPE } from '../../models/FlowModel';

    const VIDEO_BACKGROUND_LANDSCAPE = 'video_background_landscape';
    const VIDEO_BACKGROUND_PORTRAIT = 'video_background_portrait';
    const LANDSCAPE_IMG_ATTR = 'primary_background_landscape';
    const PORTRRAIT_IMG_ATTR = 'primary_background_portrait';

    const IMAGE_HOST = CBS.Registry.imageHost || 'https://wwwimage-secure.cbsstatic.com';

    export default {
        name: 'ShowsPicker',

        components: {
            ShowSelectItem,
            ButtonCta,
            AvatarImage,
        },
        computed: {
            ...mapGetters({
                featureFlagIsActive: 'featureFlags/isActive',
            }),
            isColdStartPremiumShowPickerEnabled() {
                return this.featureFlagIsActive(COLD_START_PREMIUM_LOCKS_ENABLED);
            },
            ...mapGetters(['showspicker/compilingPageAttributes'], 'showspicker/inSignupProcess'),
            ...mapState('user', {
                user: (state) => state.user,
            }),
            ...mapState({
                availableAvatarsData: (state) => state.profile.availableAvatars,
                currentProfile: (state) => state.profile.currentProfile,
            }),
            ...mapGetters({
                inSignupProcess: 'showspicker/inSignupProcess',
            }),
            classList: function () {
                return [
                    'show-picker-container',
                    'loading_page_and_api',
                    this.inSignupProcess ? 'padded-container' : '',
                ];
            },
            title() {
                let displayName;
                let titleCaption = this.$getLocale(
                    'lets_personalize_your_experience',
                ).toLowerCase();
                // we either get the name from the user store (when users are first signing up)
                // or we get the name from the profile store when users are managing their accounts
                // this is needed because there are different Vue instances for the signup flow and for account management
                if (this.$store.state.user) {
                    displayName =
                        this.$store.state.user.profile.first_name +
                        ' ' +
                        this.$store.state.user.profile.last_name;
                } else if (this.$store.state.profile.currentProfile) {
                    displayName = this.$store.state.profile.currentProfile.name;
                } else {
                    displayName = '';
                }

                displayName = displayName.slice(0, 16);

                return `${displayName}, ${titleCaption}`;
            },
            avatarUrl() {
                if (this.currentProfile && this.currentProfile.profilePicPath) {
                    return this.currentProfile.profilePicPath;
                } else if (this.availableAvatarsData && this.availableAvatarsData[0]) {
                    return this.availableAvatarsData[0].filepath_avatar;
                } else {
                    return false;
                }
            },

            notSafari() {
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                return !isSafari;
            },

            backgroundVideoLandscape() {
                const attribute = this.getPageAttribute(VIDEO_BACKGROUND_LANDSCAPE);

                if (attribute.length > 0) {
                    return attribute;
                } else {
                    return '';
                }
            },

            backgroundVideoPortrait() {
                const attribute = this.getPageAttribute(VIDEO_BACKGROUND_PORTRAIT);

                if (attribute.length > 0) {
                    return attribute;
                } else {
                    return '';
                }
            },

            landscapePoster() {
                const attribute = this.getPageAttribute(LANDSCAPE_IMG_ATTR);
                if (attribute.length > 0) {
                    return `${IMAGE_HOST}/base/${attribute}`;
                }
            },
            portraitPoster() {
                const attribute = this.getPageAttribute(PORTRRAIT_IMG_ATTR);
                if (attribute.length > 0) {
                    return `${IMAGE_HOST}/base/${attribute}`;
                }
            },

            loadLandscape() {
                if (
                    this.backgroundVideoLandscape !== '' &&
                    window.innerHeight < window.innerWidth
                ) {
                    return true;
                } else {
                    return false;
                }
            },

            description() {
                return this.$getLocale('show_picker_description');
            },

            loadPortrait() {
                if (this.backgroundVideoPortrait !== '' && window.innerHeight > window.innerWidth) {
                    return true;
                } else {
                    return false;
                }
            },
        },

        data() {
            return {
                httpService: null,
                recommendedShows: [],
                selectedShowIds: [],
                selectedShowTrackingData: [],
                minimumSelectedCount: 3,
                selectionRequired: true,
                btnNextConfig: {
                    displayText: this.$getLocale('next'),
                    href: '#',
                    event: (e) => {
                        this.onNextClicked();
                    },
                    styleClass: ['button', 'primary', 'disabled'],
                },
                //START AB Test PLGRWEB-972 - Add genres to show picker page tracking
                showPickerGenreCountTrackingObj: {},
                //END AB Test PLGRWEB-972 - Add genres to show picker page tracking
            };
        },

        created() {
            this.httpService = new HttpService();
            this.httpService.regionBaseUrl = CBS.Registry.region.prefix;
            this.$store.dispatch('profile/getAvailableAvatars').catch((error) => {
                // do nothing
            });
            this.$store.dispatch('profile/syncProfilesAndParentalControls').catch((error) => {
                // do nothing if user is not logged in
            });
            this.fetchData();

            // do not remove just comment out if there is no test running
            // this.$store.dispatch('variant/loadShowsPickerVariants').then(() => {
            //add implentation method here
            // })
        },

        mounted() {
            const sectionHeaderAction = 'setSectionHeader';
            const actionExists =
                Object.keys(this.$store._actions).findIndex(
                    (key) => key === sectionHeaderAction,
                ) !== -1;
            if (actionExists) {
                this.$store.commit(sectionHeaderAction, null);
            }

            if (this.getSelectionIsRequired() === false) {
                this.enableCTA();
            }
            // Load page attributes for compiling page
            this.$store.dispatch('showspicker/loadCompilingPageAttributes');
        },

        watch: {
            selectedShowIds(selectedIds) {
                if (
                    selectedIds.length < this.minimumSelectedCount &&
                    this.getSelectionIsRequired()
                ) {
                    if (this.btnNextConfig.styleClass.indexOf('disabled') < 0) {
                        this.btnNextConfig.styleClass.push('disabled');
                    }
                } else {
                    this.enableCTA();
                }
            },
        },

        methods: {
            fetchData() {
                this.httpService
                    .doGet('/shows/picker/xhr/recommendedshows/')
                    .then(({ result }) => {
                        this.recommendedShows = result.data;
                    })
                    .catch((error) => {
                        console.warn(error);
                    });
            },

            enableCTA() {
                this.btnNextConfig.styleClass = ['button', 'primary'];

                this.btnNextConfig.event = () => {
                    this.onNextClicked();
                };

                if (this.btnNextConfig.styleClass.indexOf('disabled') >= 0) {
                    this.btnNextConfig.styleClass.splice(
                        this.btnNextConfig.styleClass.indexOf('disabled'),
                        1,
                    );
                }
            },

            onShowItemSelected(show) {
                if (this.selectedShowIds.indexOf(show.showId) >= 0) {
                    this.selectedShowIds.splice(this.selectedShowIds.indexOf(show.showId), 1);
                    this.selectedShowTrackingData = this.selectedShowTrackingData.filter(
                        (trackingObject) => trackingObject.title != show.showTitle,
                    );
                } else {
                    this.selectedShowIds.push(show.showId);
                    // array for tracking parameters for show-picker
                    this.selectedShowTrackingData.push({
                        position: show.position,
                        title: show.showTitle,
                        showId: show.showId,
                        isLocked: !show.isContentAccessibleInCMS,
                    });
                }
            },

            onNextClicked() {
                // concatenate show position and list using “|” delimiter
                let selectedShowPosition = this.selectedShowTrackingData
                    .map((res) => res.position)
                    .join('|');
                let selectedShowTitle = this.selectedShowTrackingData
                    .map((res) => res.title)
                    .join('|');
                let selectedShowIds = this.selectedShowTrackingData
                    .map((res) => res.showId)
                    .join('|');
                let selectedShowsIncludeLockedContent = this.selectedShowTrackingData.findIndex(
                    (res) => res.isLocked,
                );
                selectedShowsIncludeLockedContent =
                    selectedShowsIncludeLockedContent > -1 ? '1' : '0';

                let minimumSelected = this.minimumSelectedCount;

                if (this.selectedShowIds.length >= minimumSelected) {
                    const selectedShowsCount = this.selectedShowIds.length;
                    CBS.Promises.Tracking.onLoad(function () {
                        om.trackAction('trackShowPickerSelection', {
                            showPickerPosition: selectedShowPosition,
                            showPickerSelection: selectedShowTitle,
                            showPickerSelectionCount: selectedShowsCount,
                            showPickerContentList: selectedShowIds,
                            contentLocked: selectedShowsIncludeLockedContent,
                        });
                    });
                    if (this['showspicker/compilingPageAttributes'] === null) {
                        // Load compiling page attributes if not yet loaded
                        this.$store.dispatch('showspicker/loadCompilingPageAttributes').then(() => {
                            this.redirectToCompilingPage();
                        });
                    } else {
                        this.redirectToCompilingPage();
                    }
                }
            },

            redirectToCompilingPage() {
                this.$store.commit('showspicker/setSelectedShows', this.selectedShowIds);
                if (
                    this.$store.getters['flow/type'] === FLOW_TYPE.DELTA &&
                    this.$store.getters['featureFlags/deltaJourneySignUpEnabled']
                ) {
                    this.$router.replace({ name: 'ResellerCompiling' });
                    return;
                }

                this.$router.replace({ name: 'CompilingPage' });
            },

            getPageAttribute(attributeKey) {
                const attrs = this['showspicker/compilingPageAttributes'];

                if (attrs === null || attrs.length < 1) {
                    return '';
                }

                let attrKeys = Object.keys(attrs[0]);
                if (attrKeys.includes(attributeKey)) {
                    return attrs[0][attributeKey];
                } else {
                    return '';
                }
            },

            getSelectionIsRequired() {
                return this.selectionRequired;
            },
        },
    };
</script>
